import { sumBy } from "lodash-es";
import { computed, inject, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAsyncTask } from "@/composables/useAsyncTask";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Cancellation } from "@/types/cancellation";

/**
 * Maximum number of documents one archive may hold. Mirrors MAX_BULK_DOWNLOAD_DOCUMENTS in
 * app/company_order/cancel/bulk_download.py, which the start endpoint enforces again (400).
 */
export const MAX_BULK_DOWNLOAD_DOCUMENTS = 2000;

const BYTES_PER_MB = 1024 * 1024;

/** Outcome of the bulk-download task; only "ready" has an archive to fetch. */
type BulkDownloadResult =
  | { status: "ready"; filename: string; document_count: number }
  | { status: "empty" }
  | { status: "too_large"; total_bytes: number; max_bytes: number };

/** Documents written into the archive so far, as reported by the running task. */
interface BulkDownloadProgress {
  current: number;
  total: number;
}

/** Why the download button is disabled, worded for display beside it. */
interface BulkDownloadBlockedReason {
  text: string;
  className: "text-danger" | "text-muted";
}

/**
 * Downloads the documents of the statements currently shown (every row passing the table's
 * filters) as one ZIP, built by a background task and polled to completion, and owns the state of
 * the button that starts it: whether it can start, its label, and why it is blocked.
 *
 * Only the document-count cap can be checked here; document sizes are unknown to the frontend, so a
 * selection over the byte cap is reported by the task and surfaces as a warning. A selection whose
 * documents vanished before the task ran warns and calls `refetch` instead of downloading.
 */
export function useBulkCancellationDownload(
  visibleCancellations: Ref<Cancellation[]>,
  refetch: () => void
) {
  const { t } = useI18n();
  const globalErrorHandler = inject("globalErrorHandler") as (
    error: any
  ) => void;
  const globalWarningHandler = inject("globalWarningHandler") as (
    title: string,
    description: string
  ) => void;

  const bulkDownload = useAsyncTask<BulkDownloadResult, BulkDownloadProgress>();

  // "success" lasts until handleResult resets it, which for a ready archive is only once its fetch
  // settles. Counting it as busy keeps a second click from starting a run that reset() then cancels.
  const isBulkDownloading = computed(
    () => bulkDownload.isRunning.value || bulkDownload.state.value === "success"
  );

  const downloadableCancellations = computed(() =>
    visibleCancellations.value.filter((c) => c.uploaded_document_count > 0)
  );
  const documentCount = computed(() =>
    sumBy(downloadableCancellations.value, "uploaded_document_count")
  );

  const blockedReason = computed<BulkDownloadBlockedReason | null>(() => {
    if (isBulkDownloading.value) return null;
    if (documentCount.value > MAX_BULK_DOWNLOAD_DOCUMENTS) {
      return {
        text: t("cancellation.download_too_many_documents", {
          count: documentCount.value,
          max: MAX_BULK_DOWNLOAD_DOCUMENTS,
        }),
        className: "text-danger",
      };
    }
    if (documentCount.value === 0) {
      return {
        text: t("cancellation.download_none_selected"),
        className: "text-muted",
      };
    }
    return null;
  });

  const canStart = computed(
    () => !isBulkDownloading.value && blockedReason.value === null
  );

  const label = computed(() => {
    if (isBulkDownloading.value) {
      const progress = bulkDownload.progress.value;
      // The task reports 0 before it has written anything, and "0 / N" reads as stuck: show counts
      // only once documents are actually going into the archive.
      if (!progress?.current) {
        return t("cancellation.download_preparing_documents");
      }
      return t("cancellation.download_preparing", {
        current: progress.current,
        total: progress.total,
      });
    }
    return t(
      "cancellation.download_shown",
      { count: documentCount.value },
      documentCount.value
    );
  });

  // Every outcome is reported by the state watcher below, including a failure to start.
  const startBulkDownload = async () => {
    if (!canStart.value) return;
    const cancellationIds = downloadableCancellations.value.map((c) => c.id);
    await bulkDownload.execute(async () => {
      const response = await goSolidApi.post("/api/cancellations/download", {
        cancellation_ids: cancellationIds,
        // Names the archive's day folders, so they match the dates shown and filtered here.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      return response.data;
    });
  };

  const warnTooLarge = (
    result: Extract<BulkDownloadResult, { status: "too_large" }>
  ) => {
    globalWarningHandler(
      t("cancellation.download_too_large_title"),
      t("cancellation.download_too_many_bytes", {
        // Rounded up, so a selection just over the cap never reads as equal to it.
        total: Math.ceil(result.total_bytes / BYTES_PER_MB),
        max: Math.floor(result.max_bytes / BYTES_PER_MB),
      })
    );
  };

  const warnEmpty = () => {
    globalWarningHandler(
      t("cancellation.bulk_download_no_documents_title"),
      t("cancellation.bulk_download_no_documents")
    );
    refetch();
  };

  const handleResult = (result: BulkDownloadResult) => {
    switch (result.status) {
      case "ready":
        bulkDownload.downloadResult().finally(() => bulkDownload.reset());
        return;
      case "too_large":
        warnTooLarge(result);
        break;
      case "empty":
        warnEmpty();
        break;
      default: {
        // A status newer than this bundle: report it instead of failing silently in the watcher.
        const unexpected: never = result;
        globalErrorHandler(
          new Error(
            `Unexpected bulk download result: ${JSON.stringify(unexpected)}`
          )
        );
      }
    }
    bulkDownload.reset();
  };

  watch(bulkDownload.state, (newState) => {
    if (newState === "success") {
      // useAsyncTask only reports success with a result, and this watcher runs after both are set.
      handleResult(bulkDownload.result.value!);
    } else if (newState === "failed") {
      globalErrorHandler(
        bulkDownload.error.value ??
          bulkDownload.errorMessage.value ??
          t("cancellation.bulk_download_failed")
      );
      bulkDownload.reset();
    }
  });

  return {
    isBulkDownloading,
    canStart,
    blockedReason,
    label,
    startBulkDownload,
  };
}
